/**
 * The widget, in order: pane · chips + checklist · composer · meter · gate card
 * · CTA bar. Everything here is eager.
 *
 * Two rules the layout exists to keep:
 *
 * 1. **The composer never moves.** The pane opens *above* it and the page
 *    scrolls by exactly the pane's height in the same commit — the measurement
 *    and the scroll live in the controller. There is deliberately **no CSS
 *    height transition**: an animated height means the "after" measurement is
 *    taken mid-animation and the compensation is wrong for every frame of it.
 *
 * 2. **Nothing narrows.** The pane, the composer and the CTA bar span the full
 *    section width. A side column was tried in v10 and rejected; do not
 *    reintroduce it.
 *
 * The thread is behind `React.lazy` — the app's first split point — because the
 * assistant-ui runtime is the single largest thing this feature adds and no
 * visitor who never asks a question should pay for it. It is warmed on the
 * first sign of intent and, failing that, when the browser next goes idle.
 */

import { Lock, RotateCcw } from 'lucide-react'
import { lazy, Suspense, useEffect, useMemo, useRef } from 'react'
import { useAskProfile } from '../context/ask-profile-context'
import { AskProfileChips } from './ask-profile-chips'
import { AskProfileComposer } from './ask-profile-composer'
import { AskProfileDoorDialog } from './ask-profile-door-dialog'
import { AskProfileDoors } from './ask-profile-doors'
import { AskProfilePaneSkeleton } from './ask-profile-pane-skeleton'
import { AskProfileSources, toSourceChecklist } from './ask-profile-sources'

const AskProfileThread = lazy(() => import('./ask-profile-thread'))

/**
 * Start the download without mounting anything. `React.lazy` memoises the
 * promise, so calling this and then rendering the component costs one request.
 */
function warmThreadChunk() {
  void import('./ask-profile-thread')
}

/** The ask-profile panel. `doors` is the investor's "Track this startup" line; the app's Follow control replaces it. */
export function AskProfileSection({ doors = true }: { doors?: boolean }) {
  const ask = useAskProfile()
  const warmedRef = useRef(false)
  const checklist = useMemo(
    () => toSourceChecklist(ask.sources, ask.slideCount, ask.documentCount),
    [ask.sources, ask.slideCount, ask.documentCount]
  )

  const warm = () => {
    if (warmedRef.current) return
    warmedRef.current = true
    warmThreadChunk()
  }

  // Idle, never eagerly: the deck's first PDF render is happening on the same
  // main thread and it is what the visitor is actually looking at.
  useEffect(() => {
    if (warmedRef.current) return
    if (typeof window.requestIdleCallback !== 'function') return
    const handle = window.requestIdleCallback(() => {
      if (warmedRef.current) return
      warmedRef.current = true
      warmThreadChunk()
    })
    return () => window.cancelIdleCallback(handle)
  }, [])

  return (
    <div className="ask-profile-anchor-none" data-ask-profile-section>
      {/* Two columns, so the two headings start on the same line and the taller
          of them does not leave a hole beside the shorter. The label is
          rendered here rather than by `Section`'s `title` prop precisely so it
          can share a row with the sources.
          The chips live in the LEFT column rather than in a row of their own:
          the sources column is three pills tall, and anything else would leave
          that height empty. */}
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-[1fr_auto]">
        <div>
          <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Ask the profile
          </h3>
          {/* The chips are the empty state's instruction. Once there is an
              answer to read they have done their job. */}
          {ask.paneHasContent ? (
            // Clearing is a view reset, not an erasure: the turn ledger is
            // untouched.
            <button
              type="button"
              onClick={ask.clearChat}
              className="flex cursor-pointer items-center gap-1.5 py-1.5 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
            >
              <RotateCcw className="size-3.5" aria-hidden />
              Clear chat
            </button>
          ) : (
            <AskProfileChips onWarm={warm} />
          )}
        </div>
        <AskProfileSources rows={checklist} showDetail={!ask.paneHasContent} />
      </div>
      {ask.paneHasContent && (
        <div
          className={[
            'ask-profile-anchor-none mt-2 flex flex-col overflow-hidden rounded-xl',
            'border border-border bg-[#111112]',
            // Sized by its content up to a ceiling, never a fixed box: a lone
            // first question in a 600px pane is mostly empty. The ceiling is
            // where the thread starts scrolling instead of the page. 62vh on
            // iOS Safari resolves against the LARGE viewport, so the dvh cap
            // is what keeps the pane inside the space that actually exists
            // once the browser chrome is on screen.
            'max-h-[62dvh] md:max-h-[600px]',
          ].join(' ')}
        >
          <Suspense fallback={<AskProfilePaneSkeleton />}>
            <AskProfileThread />
          </Suspense>

          {/* Asked while another answer was running. Shown the instant they are
              typed, because a question that disappears on Enter and returns a
              minute later reads as a dropped message rather than a queued one.
              Rendered here in the eager shell rather than in the thread: the
              runtime has not been told about them yet, and telling it would
              cancel the answer currently being written. */}
          {ask.pendingQuestions.length > 0 && (
            <ul className="flex flex-none flex-col gap-2 border-t border-border px-3 py-2.5">
              {ask.pendingQuestions.map((question, index) => (
                <li
                  // Position IS the identity here: the same question can be
                  // queued twice, and the list only ever shifts from the front.
                  // biome-ignore lint/suspicious/noArrayIndexKey: see above
                  key={`${question}-${index}`}
                  className="flex justify-end text-[12.5px] text-muted-foreground"
                >
                  {/* The dashed edge is the whole signal: same bubble shape and
                      position as a sent question, drawn as not-yet-sent. A word
                      would only repeat what the outline already says. */}
                  <span className="rounded-[14px_14px_4px_14px] border border-dashed border-border px-3 py-1.5 sm:max-w-[80%]">
                    {question}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <AskProfileComposer onWarm={warm} />
      {/* The question meter is gone with the cap, but this half of that row is
          not a quota artifact — it is the promise the whole surface rests on,
          and it is the reason an investor asks a real question instead of a
          safe one. */}
      <p className="mt-2 flex items-center justify-end gap-1.5 text-right text-[12.5px] text-muted-foreground">
        <Lock className="size-3.5" aria-hidden />
        The founder never sees what you ask.
      </p>
      {/* Investor-only: see AskProfileAudience. */}
      {ask.audience === 'investor' && doors && (
        <>
          <AskProfileDoors />
          <AskProfileDoorDialog />
        </>
      )}
    </div>
  )
}
